import {useEffect, useState} from 'react';
import type {DataGridPremiumProps} from '@mui/x-data-grid-premium';
import {DataGridPremium, useGridApiRef} from '@mui/x-data-grid-premium';
import '~/components/muix-data-grid/BaseTableMuiX.scss';
import {
    buildDataGridClassName,
    DATA_GRID_COLUMN_HEADER_HEIGHT,
    DATA_GRID_DEFAULT_PAGE_SIZE,
    DATA_GRID_DENSITY,
    DATA_GRID_HEADER_FILTER_HEIGHT,
    DATA_GRID_PAGE_SIZE_OPTIONS,
    DATA_GRID_ROW_MIN_HEIGHT,
    type DataGridRowSize,
    EMPTY_FILTER_MODEL,
    getDataGridGroupingRowClassName,
    getDataGridHeightForCompactMin,
    getDataGridHeightForDensity,
    getDataGridRowMinHeight,
} from './data-grid.config';
import {DataGridToolbar, type DataGridToolbarProps} from './DataGridToolbar';
import {DataGridPagination, type DataGridPaginationProps} from './DataGridPagination';
import {autosizeFieldsWithoutSavedWidth, usePersistedDataGridLayout} from './usePersistedDataGridLayout';

export type {DataGridRowSize} from './data-grid.config';
export {
    DATA_GRID_COLUMN_HEADER_HEIGHT,
    DATA_GRID_DEFAULT_PAGE_SIZE,
    DATA_GRID_HEADER_FILTER_HEIGHT,
    DATA_GRID_PAGE_SIZE_OPTIONS,
    DATA_GRID_ROW_MIN_HEIGHT,
    EMPTY_FILTER_MODEL,
};
export {DataGridToolbar, type DataGridToolbarProps} from './DataGridToolbar';
export {DataGridHeaderFiltersToggleButton} from './DataGridHeaderFiltersToggleButton';
export {DataGridPagination, type DataGridPaginationProps} from './DataGridPagination';

export type CustomDataGridProps = Omit<DataGridPremiumProps, 'rowHeight' | 'headerFilters'> & {
    /** Row min height: small = 36px, medium = 72px. `density` scales row height on top of this. */
    rowSize?: DataGridRowSize;
    /** When true, rows grow with content using getRowHeight={() => 'auto'}. */
    autoRowHeight?: boolean;
    /** Props forwarded to `DataGridToolbar` (requires `showToolbar`). */
    toolbarProps?: DataGridToolbarProps;
    /** Props forwarded to `DataGridPagination`. */
    paginationProps?: DataGridPaginationProps;
    /**
     * localStorage id for this grid's column layout (width, order, visibility, pinning).
     * When omitted, grids that share the same column fields share one layout.
     */
    layoutKey?: string;
    /**
     * Persist column layout in localStorage. Turn off for a grid that already stores
     * widths, order, visibility, or pinning itself.
     */
    persistLayout?: boolean;
    /**
     * Opt in to row virtualization (off by default, as it always has been).
     * Required for features that depend on the viewport, such as the data
     * source's lazy loading.
     */
    virtualized?: boolean;
    /**
     * Opt in to header filters (off by default, as it always has been).
     */
    headerFilters?: boolean;
};

export const CustomDataGrid = ({
                                   rowSize = 'small',
                                   autoRowHeight = false,
                                   virtualized = false,
                                   className,
                                   getRowHeight,
                                   getRowClassName,
                                   headerFilterHeight: headerFilterHeightProp,
                                   columnHeaderHeight: columnHeaderHeightProp,
                                   columnGroupHeaderHeight: columnGroupHeaderHeightProp,
                                   apiRef: apiRefProp,
                                   slots,
                                   slotProps,
                                   toolbarProps,
                                   paginationProps,
                                   pagination = true,
                                   pageSizeOptions = DATA_GRID_PAGE_SIZE_OPTIONS,
                                   paginationModel,
                                   initialState,
                                   layoutKey,
                                   persistLayout = true,
                                   columns,
                                   autosizeOnMount,
                                   autosizeOptions,
                                   onColumnWidthChange,
                                   onColumnOrderChange,
                                   onColumnVisibilityModelChange,
                                   onPinnedColumnsChange,
                                   density = DATA_GRID_DENSITY,
                                   headerFilters = false,
                                   disableVirtualization,
                                   ...props
                               }: CustomDataGridProps) => {
    const apiRefInternal = useGridApiRef();
    const apiRef = apiRefProp ?? apiRefInternal;
    const [headerFiltersVisible, setHeaderFiltersVisible] = useState(headerFilters);
    const minHeight = getDataGridRowMinHeight(rowSize);
    const columnHeaderHeight = columnHeaderHeightProp
        ?? getDataGridHeightForDensity(DATA_GRID_COLUMN_HEADER_HEIGHT, density);
    const columnGroupHeaderHeight = columnGroupHeaderHeightProp
        ?? getDataGridHeightForDensity(DATA_GRID_COLUMN_HEADER_HEIGHT, density);
    const headerFilterHeight = headerFilterHeightProp
        ?? getDataGridHeightForDensity(DATA_GRID_HEADER_FILTER_HEIGHT, density);
    const mergedClassName = [buildDataGridClassName({minHeight, autoRowHeight}), className]
        .filter(Boolean)
        .join(' ');
    const persistedLayout = usePersistedDataGridLayout({
        layoutKey,
        columns,
        initialState,
        persistLayout,
        onColumnWidthChange,
        onColumnOrderChange,
        onColumnVisibilityModelChange,
        onPinnedColumnsChange,
    });
    const limitAutosize = persistLayout && (Boolean(autosizeOnMount) || autosizeOptions?.columns != null);
    const autosizeFields = limitAutosize
        ? autosizeFieldsWithoutSavedWidth(
            columns.map(column => column.field),
            persistedLayout.savedDimensions,
            autosizeOptions?.columns,
        )
        : undefined;
    const noteUserResize = persistedLayout.noteUserResize;
    useEffect(() => {
        if (!persistLayout) return undefined;
        const api = apiRef.current;
        if (!api?.subscribeEvent) return undefined;
        const unsubStart = api.subscribeEvent('columnResizeStart', ({field}) => {
            noteUserResize(field);
        });
        const unsubStop = api.subscribeEvent('columnResizeStop', () => {
            noteUserResize(null);
        });
        return () => {
            unsubStart();
            unsubStop();
        };
    }, [apiRef, persistLayout, noteUserResize]);
    // An uncontrolled grid starts on the default page size; a caller's own initial value wins.
    const mergedInitialState = paginationModel ? persistedLayout.initialState : {
        ...persistedLayout.initialState,
        pagination: {
            ...persistedLayout.initialState?.pagination,
            paginationModel: {
                pageSize: DATA_GRID_DEFAULT_PAGE_SIZE,
                ...persistedLayout.initialState?.pagination?.paginationModel,
            },
        },
    };

    return (
        <DataGridPremium
            disableColumnMenu={true}
            {...props}
            apiRef={apiRef}
            density={density}
            rowHeight={getDataGridHeightForCompactMin(minHeight)}
            columnHeaderHeight={columnHeaderHeight}
            columnGroupHeaderHeight={columnGroupHeaderHeight}
            headerFilters={headerFiltersVisible}
            headerFilterHeight={headerFilterHeight}
            className={mergedClassName}
            pagination={pagination}
            pageSizeOptions={pageSizeOptions}
            columns={persistedLayout.columns as typeof columns}
            autosizeOnMount={Boolean(autosizeOnMount) && (autosizeFields?.length ?? 1) > 0}
            autosizeOptions={autosizeFields ? {...autosizeOptions, columns: autosizeFields} : autosizeOptions}
            paginationModel={paginationModel}
            initialState={mergedInitialState}
            onColumnWidthChange={persistedLayout.onColumnWidthChange}
            onColumnOrderChange={persistedLayout.onColumnOrderChange}
            onColumnVisibilityModelChange={persistedLayout.onColumnVisibilityModelChange}
            onPinnedColumnsChange={persistedLayout.onPinnedColumnsChange}
            getRowHeight={autoRowHeight ? () => 'auto' : getRowHeight}
            disableVirtualization={disableVirtualization ?? !virtualized}
            slots={{
                toolbar: DataGridToolbar,
                pagination: DataGridPagination,
                columnHeaderFilterIconButton: () => null,
                ...slots,
            }}

            slotProps={{
                ...slotProps,
                toolbar: {
                    ...slotProps?.toolbar,
                    ...toolbarProps,
                    headerFiltersVisible,
                    onToggleHeaderFilters: () => setHeaderFiltersVisible(visible => !visible),
                } as unknown as NonNullable<DataGridPremiumProps['slotProps']>['toolbar'],
                pagination: {
                    ...slotProps?.pagination,
                    ...paginationProps,
                },
            }}
            getRowClassName={(params) => {
                const groupingClassName = getDataGridGroupingRowClassName(apiRef, params);
                const userClassName = getRowClassName?.(params) ?? '';

                return [groupingClassName, userClassName].filter(Boolean).join(' ');
            }}
        />
    );
};
